import * as Service from 'types/service';
import { ChronoUnit, Duration, Instant } from 'js-joda';
import { Component } from 'react';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { formatDurationAsHMText } from 'utils/timeUtils';
import {
  isPartyArrived,
  isPartyExpected,
  isPartyPartiallyArrived,
  isPartyQuotedWalkIn,
} from 'utils/partyUtils';
import clsx from 'clsx';

interface Props {
  className?: string;
  party: Service.Party;
}

class PartyDuration extends Component<Props & IWithContextProps> {
  _updateInterval: NodeJS.Timeout | null;

  componentDidMount(): void {
    if (!this._updateInterval) {
      // On a loop, force a re-render so as to increment the timer
      this._updateInterval = setInterval(() => {
        this.forceUpdate();
      }, 1000 * 15); // 15 seconds
    }
  }

  componentWillUnmount(): void {
    if (this._updateInterval) {
      clearInterval(this._updateInterval);
      this._updateInterval = null;
    }
  }

  render() {
    const {
      className,
      party,
      context: { isFeatureEnabled },
    } = this.props;

    if (!party.arrivalTime) {
      return null;
    }

    const arrivalInstant = Instant.ofEpochSecond(party.arrivalTime, 0);
    let timeString;
    let isLate;

    // If a party has arrived we show the hours and minutes since arrival
    if (!isPartyQuotedWalkIn(party) && (isPartyPartiallyArrived(party) || isPartyArrived(party))) {
      const arrivalString = formatDurationAsHMText(
        Duration.ofSeconds(arrivalInstant.until(Instant.now(), ChronoUnit.SECONDS))
      );
      timeString = `+${arrivalString}`;
    }

    // If a walk-in is expected and has a quoted time we show how long they have been waiting
    const hasQuotedTimeMinutes = isFeatureEnabled('FLEX_EXPECTED_WALK_INS')
      ? party.quotedTimeMinutes !== undefined
      : !!party.quotedTimeMinutes;
    if (isPartyQuotedWalkIn(party) && isPartyExpected(party) && hasQuotedTimeMinutes) {
      const secondsWaited = Duration.ofSeconds(
        arrivalInstant.until(Instant.now(), ChronoUnit.SECONDS)
      );
      isLate = secondsWaited.toMinutes() > (party.quotedTimeMinutes ?? 0);
      timeString = `+${formatDurationAsHMText(secondsWaited.abs())}/${party.quotedTimeMinutes}m`;
    }

    return !timeString ? null : (
      <div
        className={clsx('PartyDuration', className, {
          'is-late': isLate,
        })}
      >
        {timeString}
      </div>
    );
  }
}

export default withContext(PartyDuration);
